<template>
  <div class="flex w-full justify-center">
    <div class="m-4 w-1/2 min-w-min max-w-4xl text-black">
      <Breadcrumb :breadcrumbs="breadcrumbs" class="mb-4"/>
      <div class="flex pb-4">
        <div class="grow">
          <span v-show="!current_id" class="text-2xl font-bold">{{ t( 'Create customer site' ) }}</span>
          <span v-show="current_id" class="text-2xl font-bold">{{ t( 'Update customer site' ) }}</span>
        </div>
        <div class="flex gap-2">
          <LoadingButton v-if="current_id && is_deleteable && is_delete_auth()" :label="t( 'Delete' )" ref="delete_btn" @click="click_to_delete()" colors="bg-red-600 text-white hover:bg-red-700"></LoadingButton>
          <LoadingButton v-if="is_create_or_update_auth()" :label="t( 'Save' )" ref="save_btn" @click="click_to_save()"></LoadingButton>
        </div>
      </div>
      <!-- Name -->
      <div class="mb-4 rounded-md shadow-md">
        <label class="mb-2 block bg-gray-100 p-2 font-bold" for="name">{{ t( 'Name' ) }}</label>
        <InputTextRequired ref="input_name" name="name" v-model="input.name" :show_required="input_name_required" @change="input_name_required = false" :key="key_rerender"></InputTextRequired>
      </div>
      <!-- Contacts -->
      <div class="mb-4 rounded-md shadow-md">
        <div class="flex w-full items-center bg-gray-100">
          <label class="block grow p-2 font-bold" for="name">{{ t( 'Contacts' ) }}
            <span class="inline-flex items-center rounded-md bg-green-800 px-1.5 py-0.5 text-xs font-medium text-white ring-1 ring-inset ring-indigo-700/10">{{ contact_items.length }}</span>
          </label>
          <div v-if="is_create_or_update_auth()">
            <IconEditButton :tooltip="t( 'Add new contact' )" @click="add_contacts">
              <PlusBoxIcon></PlusBoxIcon>
            </IconEditButton>
          </div>
        </div>
        <EasyDataTable
          buttons-pagination alternating
          table-class-name="customize-table"
          :empty-message="t( 'No Available Data' )"
          :headers="contact_headers"
          :items="contact_items"
        >
          <template #item-operation="item">
            <div class="operation-wrapper">
              <DeleteOutlineIcon v-if="is_create_or_update_auth()" @click="remove_contact(item)"></DeleteOutlineIcon>
            </div>
          </template>
        </EasyDataTable>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useModal } from 'vue-final-modal';
import { useRoute } from 'vue-router';
import router from '@/router';
import store from '@/store';
import axios from '@/features/axios';
import utils from '@/features/utils';
import IconEditButton from '@/components/buttons/IconEditButton.vue';
import AddContactsModal from '@/components/modal/AddContactsModal.vue';
import ConfirmModal from '@/components/modal/ConfirmModal.vue';
import API_RIGHTS from '@/constants/rights.constants';

import DeleteOutlineIcon from 'vue-material-design-icons/DeleteOutline.vue';
import PlusBoxIcon from 'vue-material-design-icons/PlusBox.vue';

export default {
  name: 'customer-site-item-view',
  components: {
    IconEditButton,
    DeleteOutlineIcon,
    PlusBoxIcon
  },
  setup() {
    const { t } = useI18n();
    const route = useRoute();
    const current_id = utils.get_params_id( route.params.id );
    const breadcrumbs = ref ( [
      { label: t( 'Administration' ) },
      { label: t( 'Customers' ), route: '/customers' },
      { label: '', route: '/customers/' + route.params.parent }
    ] );
    const input = ref( { name: '' } );
    const save_btn = ref( null );
    const delete_btn = ref( null );
    const input_name = ref( null );
    const input_name_required = ref( null );
    const key_rerender = ref ( 0 );
    const is_deleteable = ref( false ) ;
    const contact_headers = ref( [
      { text: t( 'Lastname' ), value: 'lastname', sortable: true },
      { text: t( 'Firstname' ), value: 'firstname', sortable: true },
      { text: t( 'Email' ), value: 'email', sortable: true },
      { text: '', value: 'operation', sortable: false, width: 20 }
    ] );
    const contact_items = ref( [] );

    const force_init_rerender = () => {
      key_rerender.value += 1;
    };

    const initialize = () => {
      axios.get( 'api/customer/' + route.params.parent, { headers: { 'auth-token': store.getters.getToken } } )
        .then( async response => {
          if ( response.status == 200 ) {
            breadcrumbs.value[ 2 ].label = response.data.name;
          } else {
            utils.show_alert_from_resp( t, response );
            router.push( '/customers/' + route.params.parent );
          }
        } )
        .catch( e => {
          analyse_catch( e );
        } );

      if ( current_id ) {
        axios.get( 'api/customer_site/' + current_id, { headers: { 'auth-token': store.getters.getToken } } )
          .then( async response => {
            if ( response.status == 200 ) {
              input.value.name = response.data.name;
              is_deleteable.value = response.data.is_deleteable;
              let promises = [];
              for await ( const id_contact of response.data.contacts ) {
                let promise = axios.get( 'api/contact/' + id_contact, { headers: { 'auth-token': store.getters.getToken } } )
                  .then( async response => {
                    if ( response.status == 200 ) {
                      contact_items.value.push( { value: id_contact, ...response.data } );
                    } else {
                      utils.show_alert_from_resp( t, response );
                      router.push( '/customers' );
                    }
                  } )
                  .catch( e => {
                    analyse_catch( e );
                  } );
                if ( promise ) {
                  promises.push( promise );
                }
              }
              Promise.all( promises ).then( () => {
                force_init_rerender();
              } );
            } else {
              utils.show_alert_from_resp( t, response );
              router.push( '/customers/' + route.params.parent );
            }
          } )
          .catch( e => {
            analyse_catch( e );
          } );
      }
    };

    const click_to_save = () => {
      if ( typeof ( input.value.name ) != 'string' || input.value.name.trim() == '' ) {
        input_name_required.value = true;
        input_name.value.focus();
        save_btn.value.stop_loading();
        return;
      }

      input_name_required.value = false;

      let contacts = contact_items.value.map( item => { return item.value; } );

      if ( current_id ) {
        update_customer_site( contacts );
      } else {
        create_new_customer_site( contacts );
      }
    };

    const create_new_customer_site = contacts => {
      let data = {
        name: input.value.name,
        customers: route.params.parent,
        contacts: contacts.length > 0 ? contacts : undefined
      };

      axios.post( 'api/customer_site/', data, { headers: { 'auth-token': store.getters.getToken } } )
        .then( response => {
          if ( response.status == 200 ) {
            router.push( '/customers/' + route.params.parent );
          } else {
            utils.show_alert_from_resp( t, response );
          }
          save_btn.value.stop_loading();
        } )
        .catch( e => {
          save_btn.value.stop_loading();
          analyse_catch( e );
        } );
    };

    const update_customer_site = contacts => {
      let data = {
        name: input.value.name,
        customers: route.params.parent,
        contacts: { set: contacts }
      };

      axios.put( 'api/customer_site/' + current_id, data, { headers: { 'auth-token': store.getters.getToken } } )
        .then( response => {
          if ( response.status == 201 ) {
            router.push( '/customers/' + route.params.parent );
          } else {
            utils.show_alert_from_resp( t, response );
          }
          save_btn.value.stop_loading();
        } )
        .catch( e => {
          save_btn.value.stop_loading();
          analyse_catch( e );
        } );
    };

    const click_to_delete = () => {
      confirm_delete_modal.open();
    };

    const confirm_delete_modal = useModal( {
      component: ConfirmModal,
      attrs: {
        title: t( 'Delete customer site' ),
        message: t( 'Confirm delete customer site' ),
        onConfirm() {
          delete_customer_site();
          confirm_delete_modal.close();
        },
        onCancel() {
          confirm_delete_modal.close();
          delete_btn.value.stop_loading();
        }
      }
    } );

    const delete_customer_site = () => {
      axios.delete( 'api/customer_site/' + current_id, { headers: { 'auth-token': store.getters.getToken } } )
        .then( response => {
          if ( response.status == 201 ) {
            router.push( '/customers/' + route.params.parent );
          } else {
            utils.show_alert_from_resp( t, response );
          }
          delete_btn.value.stop_loading();
        } )
        .catch( e => {
          delete_btn.value.stop_loading();
          analyse_catch( e );
        } );
    };

    const add_contacts = () => {
      add_contacts_modal.open();
    };

    const remove_contact = item => {
      let idx = contact_items.value.findIndex( it => it.value == item.value );
      contact_items.value.splice( idx, 1 );
    };

    const add_contacts_modal = useModal( {
      component: AddContactsModal,
      attrs: {
        contacts: contact_items,
        onCancel() {
          add_contacts_modal.close();
        },
        onSave( selection ) {
          contact_items.value = contact_items.value.concat( selection );
          add_contacts_modal.close();
        }
      }
    } );

    const is_create_auth = () => {
      return utils.is_auth( [ API_RIGHTS.CREATE_CUSTOMER_SITES, API_RIGHTS.GET_CONTACTS ] );
    };

    const is_update_auth = () => {
      return utils.is_auth( [ API_RIGHTS.UPDATE_CUSTOMER_SITES, API_RIGHTS.GET_CONTACTS ] );
    };

    const is_delete_auth = () => {
      return utils.is_auth( [ API_RIGHTS.DELETE_CUSTOMER_SITES ] );
    };

    const is_create_or_update_auth = () => {
      return current_id && is_update_auth() || !current_id && is_create_auth();
    };

    const analyse_catch = e => {
      utils.analyse_catch( e, router, '/customers/' + route.params.parent );
    };

    initialize();

    return { t, current_id, key_rerender, is_deleteable,
      save_btn, delete_btn, input_name, input_name_required,
      breadcrumbs, input, contact_headers, contact_items,
      is_create_auth, is_update_auth, is_delete_auth, is_create_or_update_auth,
      click_to_save, click_to_delete, add_contacts, remove_contact };
  }
};
</script>>